<template>
    <DefaultLayout
        v-bind="plainAttrs"
        :id="id"
        class="TextInput"
        input-container-class="TextInput__inputContainer"
        :class="{ 'TextInput--error': error, 'TextInput--disabled': disabled }"
        :label="label"
        :required="required"
        :error="error"
    >
        <template #default="{ id }">
            <input
                :id="id"
                class="TextInput__field"
                type="text"
                :value="displayValue"
                :placeholder="placeholder"
                :disabled="disabled"
                v-on="listeners"
            >
        </template>
        <template
            v-for="name in DEFAULT_LAYOUT_SLOT_NAMES"
            #[name]
        >
            <slot :name="name" />
        </template>
    </DefaultLayout>
</template>

<style lang="scss">
@import "../style-variables.scss";

.TextInput {
    &__inputContainer {
        @include input-container;
    }

    &__field {
        @include input-field;
    }

    &--error {
        @include input-error-state(".TextInput__inputContainer");
    }

    &--disabled {
        @include input-disabled-state(".TextInput__inputContainer");
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { omitListenerAttrs } from '@evidentid/vue-commons/utils/attrsListeners';

    import { commonInputProps } from '@/modules/dynamic-form/components/style-inputs/common-input-props';
    import DefaultLayout from '@/modules/dynamic-form/components/style-inputs/default-layout/DefaultLayout.vue';
    import { defaultLayoutSlotForwardingMixin } from '@/modules/dynamic-form/components/style-inputs/default-layout-slot-forwarding.mixin';

    type MaskFn = (value: string) => string;

    export default defineComponent({
        name: 'TextInput',
        components: { DefaultLayout },
        mixins: [ defaultLayoutSlotForwardingMixin ],
        inheritAttrs: false,
        props: {
            ...commonInputProps,
            value: {
                type: String as PropType<string>,
                default: '',
            },
            placeholder: {
                type: String as PropType<string>,
                default: '',
            },
            /**
             * Mask pattern or function.
             * - String: use '#' for digit placeholders, e.g. "##/##/####", "(###) ###-####"
             * - Function: custom formatter (value: string) => string
             */
            mask: {
                type: [ String, Function ] as PropType<string | MaskFn>,
                default: '',
            },
        },
        emits: [ 'input' ],
        computed: {
            // Handlers reach the inner element through `v-on="listeners"`. With
            // inheritAttrs left on, Vue 3 binds them to this component's root too, and a
            // native event bubbling out of the inner element then calls the caller twice.
            // Ordinary attributes still reach the root, through `plainAttrs`.
            plainAttrs(): Record<string, unknown> {
                return omitListenerAttrs(this.$attrs);
            },
            // What actually appears in the input
            displayValue(): string {
                return this.applyMask(this.value);
            },
            // Merge all listeners but override `input` with our own handler
            // Add keydown handler only when mask is present (for backspace handling)
            listeners(): Record<string, (...args: any[]) => void> {
                // Vue 3 removed the separate `$listeners` object -- parent-supplied
                // event handlers now arrive merged into `$attrs` as `onEventName`.
                // Rebuild the bare-event-name shape `v-on="listeners"` expects.
                const attrListeners = Object.entries(this.$attrs).reduce((acc, [ key, value ]) => {
                    if (/^on[A-Z]/.test(key) && typeof value === 'function') {
                        const eventName = key[2].toLowerCase() + key.slice(3);
                        acc[eventName] = value as (...args: any[]) => void;
                    }
                    return acc;
                }, {} as Record<string, (...args: any[]) => void>);
                const result: Record<string, (...args: any[]) => void> = {
                    ...attrListeners,
                    input: this.onInput,
                };

                if (this.mask && typeof this.mask !== 'function') {
                    result.keydown = this.onKeyDown;
                }

                return result;
            },
        },
        methods: {
            applyMask(value: string): string {
                if (!this.mask) {
                    return value;
                }

                // Custom function mask
                if (typeof this.mask === 'function') {
                    return (this.mask as MaskFn)(value);
                }

                // String pattern mask: '#' = digit
                const pattern = this.mask as string;
                const digits = (value || '').replace(/\D/g, '');
                let result = '';
                let digitIndex = 0;

                for (const char of pattern) {
                    if (char === '#') {
                        if (digitIndex >= digits.length) {
                            break;
                        }
                        result += digits[digitIndex];
                        digitIndex += 1;
                    } else {
                        // Static mask character
                        result += char;
                    }

                    // Stop if we used all digits
                    if (digitIndex >= digits.length && char !== '#') {
                        // Allow trailing static chars only if at least one digit has been placed
                        // (otherwise, typing the first digit could instantly fill a bunch of chars)
                        // You can tweak this behavior based on your UX preference.
                    }
                }

                return result;
            },

            onInput(event: Event): void {
                const target = event.target as HTMLInputElement;
                const rawValue = target.value || '';
                const masked = this.applyMask(rawValue);

                if (masked !== rawValue) {
                    target.value = masked;
                }

                // Emit masked value for v-model
                this.$emit('input', event);
            },

            onKeyDown(event: KeyboardEvent): void {
                // Only handle backspace (mask check is done in listeners computed)
                if (event.key !== 'Backspace') {
                    return;
                }

                const target = event.target as HTMLInputElement;
                const cursorPos = target.selectionStart ?? 0;
                const selectionEnd = target.selectionEnd ?? 0;

                // Only handle single character deletion (no text selection)
                if (cursorPos !== selectionEnd || cursorPos === 0) {
                    return;
                }

                const currentValue = target.value;
                const charBeforeCursor = currentValue[cursorPos - 1];

                // If the character before cursor is a separator (not a digit), handle specially
                if (!/\d/.test(charBeforeCursor)) {
                    event.preventDefault();

                    // Get current digits
                    const currentDigits = currentValue.replace(/\D/g, '');

                    // Count digits up to cursor position
                    let digitCountBeforeCursor = 0;
                    for (let i = 0; i < cursorPos; i++) {
                        if (/\d/.test(currentValue[i])) {
                            digitCountBeforeCursor++;
                        }
                    }

                    // Remove the digit just before the separator
                    if (digitCountBeforeCursor > 0) {
                        const newDigits =
                            currentDigits.substring(0, digitCountBeforeCursor - 1) +
                            currentDigits.substring(digitCountBeforeCursor);

                        const masked = this.applyMask(newDigits);
                        target.value = masked;

                        // Calculate new cursor position (after digitCountBeforeCursor - 1 digits)
                        let newCursorPos = 0;
                        let digitsSeen = 0;
                        while (newCursorPos < masked.length && digitsSeen < digitCountBeforeCursor - 1) {
                            if (/\d/.test(masked[newCursorPos])) {
                                digitsSeen++;
                            }
                            newCursorPos++;
                        }

                        target.setSelectionRange(newCursorPos, newCursorPos);

                        // Dispatch input event so parent gets notified
                        target.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                }
            },
        },
    });
</script>
